/**
 * Add, fix, or revise one reward rule (§11.1).
 *
 * Three modes, because the two kinds of change mean different things:
 *   * **add** — a new rule.
 *   * **fix** — correct a mistake. History is recomputed as if the rule had
 *     always said this.
 *   * **revise** — the bank changed its terms on a date. The current version
 *     ends the day before and a new one starts, so past cycles keep the terms
 *     that were in force.
 *
 * Plain-language labels throughout: this form is filled in from a T&C page by
 * someone who has better things to do.
 */

import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button, Field, Input, SELECT_CLASS } from "@/components/ui/primitives";
import {
  capsApi,
  type CapChannel,
  type CapPeriod,
  type CapRule,
  type CapRuleInput,
  type CapRuleType,
  type Category,
} from "@/lib/api/client";
import { cn } from "@/lib/cn";

export type RuleFormMode = "add" | "fix" | "revise";

const SELECT = cn(SELECT_CLASS, "w-full");

const TITLES: Record<RuleFormMode, string> = {
  add: "Add a reward rule",
  fix: "Fix this rule",
  revise: "The bank changed this rule",
};

const INTROS: Record<RuleFormMode, string> = {
  add: "Copy one line of the card's reward terms. Add one rule per rate — for example a 5% online rule and a 1% rule for everything else.",
  fix: "For a mistake you made entering it. Past cycles are re-checked as if the rule had always said this.",
  revise:
    "For when the bank changes its terms. The current version stops the day before the date below; past cycles keep the old terms.",
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function RuleFormDialog({
  cardId,
  mode,
  rule,
  suggestedFrom,
  onClose,
}: {
  cardId: string;
  mode: RuleFormMode;
  rule?: CapRule;
  /** Default "in force from" for a new rule — the start of the history MyDeck can check. */
  suggestedFrom?: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: capsApi.categories,
    staleTime: 5 * 60_000,
  });

  const c = rule?.conditions ?? {};
  const [form, setForm] = useState({
    name: rule?.name ?? "",
    rule_type: (rule?.rule_type ?? "rate_cap") as CapRuleType,
    rate_percent: rule?.rate_percent ? rule.rate_percent.replace(/\.?0+$/, "") : "",
    cap_amount: rule?.cap_amount ?? "",
    period: (rule?.period ?? "statement_cycle") as CapPeriod,
    milestone_target_spend: rule?.milestone_target_spend ?? "",
    milestone_reward_note: rule?.milestone_reward_note ?? "",
    channel: (c.channel ?? "any") as CapChannel,
    category_ids: c.category_ids ?? [],
    exclude_category_ids: c.exclude_category_ids ?? [],
    merchant_pattern: c.merchant_pattern ?? "",
    exclude_merchant_pattern: c.exclude_merchant_pattern ?? "",
    min_txn_amount: c.min_txn_amount ?? "",
    valid_from:
      mode === "revise" ? today() : (rule?.valid_from ?? suggestedFrom ?? today()),
    valid_to: mode === "fix" ? (rule?.valid_to ?? "") : "",
    notes: rule?.notes ?? "",
  });
  const [error, setError] = useState<string | null>(null);

  const set =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (input: CapRuleInput) => {
      if (mode === "add") return capsApi.create(cardId, input);
      if (mode === "fix") return capsApi.correct(rule!.id, input);
      return capsApi.revise(rule!.id, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["caps"] });
      qc.invalidateQueries({ queryKey: ["plan"] });
      toast.success(
        mode === "revise" ? "New version saved. Past cycles keep the old terms." : "Rule saved.",
      );
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const money = /^\d+(\.\d{1,2})?$/;
    if (!form.name.trim()) return setError("Give the rule a name, e.g. “5% online”.");
    if (form.rule_type === "rate_cap") {
      if (!/^\d+(\.\d{1,2})?$/.test(form.rate_percent) || Number(form.rate_percent) > 100)
        return setError("The rate is a percentage, e.g. 5 or 1.5.");
      if (form.cap_amount && !money.test(form.cap_amount))
        return setError("The cap is an amount in rupees, e.g. 1500.");
    } else if (!money.test(form.milestone_target_spend)) {
      return setError("A milestone needs a spend target in rupees.");
    }
    if (form.min_txn_amount && !money.test(form.min_txn_amount))
      return setError("Minimum purchase is an amount in rupees.");

    save.mutate({
      name: form.name.trim(),
      rule_type: form.rule_type,
      rate_percent: form.rule_type === "rate_cap" ? form.rate_percent : null,
      cap_amount: form.rule_type === "rate_cap" && form.cap_amount ? form.cap_amount : null,
      period: form.period,
      milestone_target_spend:
        form.rule_type === "milestone" ? form.milestone_target_spend : null,
      milestone_reward_note:
        form.rule_type === "milestone" ? form.milestone_reward_note.trim() || null : null,
      conditions: {
        channel: form.channel,
        category_ids: form.category_ids,
        exclude_category_ids: form.exclude_category_ids,
        merchant_pattern: form.merchant_pattern.trim() || null,
        exclude_merchant_pattern: form.exclude_merchant_pattern.trim() || null,
        min_txn_amount: form.min_txn_amount || null,
      },
      valid_from: form.valid_from,
      valid_to: form.valid_to || null,
      active: true,
      notes: form.notes.trim() || null,
    });
  }

  const toggle = (key: "category_ids" | "exclude_category_ids", id: string) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id],
    }));

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[92vh] w-[min(40rem,94vw)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto dialog-content">
          <div className="flex items-start justify-between gap-3 border-b border-hairline px-5 py-4">
            <div>
              <Dialog.Title className="font-display text-lg font-semibold text-ink">
                {TITLES[mode]}
              </Dialog.Title>
              <Dialog.Description className="mt-0.5 text-xs text-muted">
                {INTROS[mode]}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <form onSubmit={submit} className="space-y-5 px-5 py-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name">
                <Input value={form.name} onChange={set("name")} placeholder="e.g. 5% online" />
              </Field>
              <Field label="Kind">
                <select className={SELECT} value={form.rule_type} onChange={set("rule_type")}>
                  <option value="rate_cap">Cashback rate</option>
                  <option value="milestone">Spend milestone</option>
                </select>
              </Field>
            </div>

            {form.rule_type === "rate_cap" ? (
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Rate (%)">
                  <Input inputMode="decimal" value={form.rate_percent} onChange={set("rate_percent")} placeholder="5" />
                </Field>
                <Field label="Max cashback (₹)" hint="Leave empty if there's no cap.">
                  <Input inputMode="decimal" value={form.cap_amount} onChange={set("cap_amount")} placeholder="No cap" />
                </Field>
                <Field label="Cap resets">
                  <PeriodSelect value={form.period} onChange={set("period")} />
                </Field>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Spend target (₹)">
                  <Input inputMode="decimal" value={form.milestone_target_spend} onChange={set("milestone_target_spend")} placeholder="10000" />
                </Field>
                <Field label="Counted per">
                  <PeriodSelect value={form.period} onChange={set("period")} />
                </Field>
                <Field label="What you get">
                  <Input value={form.milestone_reward_note} onChange={set("milestone_reward_note")} placeholder="e.g. ₹500 voucher" />
                </Field>
              </div>
            )}

            <fieldset className="space-y-3 rounded-lg border border-hairline p-3">
              <legend className="label-micro px-1">Which purchases count</legend>
              <p className="text-xs text-faint">
                Leave everything here empty for “all purchases”.
              </p>

              <Field label="Online or in a shop">
                <select className={SELECT} value={form.channel} onChange={set("channel")}>
                  <option value="any">Either</option>
                  <option value="online">Online only</option>
                  <option value="offline">In a shop only</option>
                </select>
              </Field>
              {form.channel !== "any" && !form.merchant_pattern.trim() && (
                <p className="text-xs text-warn">
                  Statements don't say whether a purchase was online, so MyDeck will
                  count every purchase toward this cap. Listing your usual
                  {form.channel === "online" ? " online " : " "}merchants below makes it exact.
                </p>
              )}

              <CategoryPicker
                label="Only these categories"
                categories={categories}
                selected={form.category_ids}
                onToggle={(id) => toggle("category_ids", id)}
              />
              <Field
                label="Only these merchants"
                hint="Names separated by | — e.g. SWIGGY|ZOMATO. Matches the bank's description of the purchase."
              >
                <Input value={form.merchant_pattern} onChange={set("merchant_pattern")} className="font-mono" placeholder="SWIGGY|ZOMATO" />
              </Field>

              <CategoryPicker
                label="Never these categories"
                categories={categories}
                selected={form.exclude_category_ids}
                onToggle={(id) => toggle("exclude_category_ids", id)}
                tone="exclude"
              />
              <Field label="Never these merchants" hint="Same format, e.g. RENT|WALLET.">
                <Input value={form.exclude_merchant_pattern} onChange={set("exclude_merchant_pattern")} className="font-mono" />
              </Field>
              <Field label="Minimum purchase (₹)" hint="Optional.">
                <Input inputMode="decimal" value={form.min_txn_amount} onChange={set("min_txn_amount")} />
              </Field>
            </fieldset>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={mode === "revise" ? "New terms start on" : "In force from"}
                hint={
                  mode === "add"
                    ? "Set this to when these terms started, so MyDeck can check them against past statements."
                    : undefined
                }
              >
                <Input type="date" value={form.valid_from} onChange={set("valid_from")} />
              </Field>
              {mode === "fix" && (
                <Field label="In force until" hint="Empty means still in force.">
                  <Input type="date" value={form.valid_to} onChange={set("valid_to")} />
                </Field>
              )}
            </div>

            <Field label="Notes (optional)" hint="Where you read this — a link or 'T&C PDF, Oct 2026'.">
              <textarea
                value={form.notes}
                onChange={set("notes")}
                rows={2}
                className="w-full rounded-lg border border-hairline bg-base px-3 py-2 text-sm text-ink focus:border-brass/50 focus:outline-none focus:ring-1 focus:ring-brass/50"
              />
            </Field>

            {error && <p className="text-sm text-overdue">{error}</p>}

            <div className="flex justify-end gap-2 border-t border-hairline pt-4">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={save.isPending}>
                {mode === "revise" ? "Save new version" : "Save rule"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PeriodSelect({
  value,
  onChange,
}: {
  value: CapPeriod;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}) {
  return (
    <select className={SELECT} value={value} onChange={onChange}>
      <option value="statement_cycle">Each statement cycle</option>
      <option value="calendar_month">Each calendar month</option>
    </select>
  );
}

function CategoryPicker({
  label,
  categories,
  selected,
  onToggle,
  tone = "include",
}: {
  label: string;
  categories: Category[] | undefined;
  selected: string[];
  onToggle: (id: string) => void;
  tone?: "include" | "exclude";
}) {
  return (
    <div className="space-y-1.5">
      <span className="label-micro block">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {categories?.map((cat) => {
          const on = selected.includes(cat.id);
          return (
            <button
              key={cat.id}
              type="button"
              aria-pressed={on}
              onClick={() => onToggle(cat.id)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50",
                on
                  ? tone === "include"
                    ? "border-brass/40 bg-brass/10 text-brass"
                    : "border-overdue/40 bg-overdue/10 text-overdue"
                  : "border-hairline text-muted hover:text-ink",
              )}
            >
              {cat.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
